// A match as a record: the seed, the mode, the casters of the player, and
// every action in order.
// The flow is a pure function of those, so the record gives back the same
// match. Each action keeps the state of the random source from before it,
// because the AI also draws from that source when it picks a spell target.

import type { CasterId } from '../content/casters';
import type { Move, Side } from '../engine/types';
import type { Rng } from '../lib/rng';
import {
  aimAt,
  cancelCast,
  castSlot,
  concede,
  createMatch,
  playMove,
  rollDie,
  settleLostRoles,
} from './flow';
import { firstStep, type HistoryStep, stepBetween } from './history';
import type { GameState, MatchMode } from './state';

export type MatchAction =
  | { kind: 'roll' }
  | { kind: 'aim'; square: number }
  /** Godsmode: a cast from a slot of the caster. */
  | { kind: 'cast'; slot: number }
  /** Godsmode: the cast that waits for its picks is taken back. */
  | { kind: 'uncast' }
  | { kind: 'move'; move: Move }
  /** The side gives up. It may be the turn of either side. */
  | { kind: 'concede'; side: Side };

export type RecordedAction = MatchAction & {
  /** The state of the random source before the action. */
  rngState: number;
};

export type MatchRecord = {
  seed: number;
  mode: MatchMode;
  /** The casters of the player, in Godsmode. The match draws the casters of the other side. */
  casters: readonly CasterId[] | null;
  /**
   * The casters of violet, when a person picked them: a match in a room. With
   * none, the match draws them, so a save from before this field loads the same.
   */
  opponentCasters?: readonly CasterId[] | null;
  actions: readonly RecordedAction[];
};

export type ReplayedMatch = {
  state: GameState;
  steps: readonly HistoryStep[];
  /** The last action made no step. See the history store. */
  liveIsAhead: boolean;
};

const runAction = (state: GameState, action: MatchAction, rng: Rng): GameState => {
  if (action.kind === 'roll') return rollDie(state, rng);
  if (action.kind === 'aim') return aimAt(state, action.square, rng);
  if (action.kind === 'cast') return castSlot(state, action.slot, rng);
  if (action.kind === 'uncast') return cancelCast(state);
  if (action.kind === 'concede') return concede(state, action.side);
  return playMove(state, action.move, rng);
};

/** Runs an action, and records a caster or a pitch the action left with no piece. */
export const applyAction = (state: GameState, action: MatchAction, rng: Rng): GameState =>
  settleLostRoles(state, runAction(state, action, rng), rng);

/**
 * Plays a record from the start. Leaves `rng` in the state the match left
 * it. Null when an action does nothing, which means the rules changed after
 * the record was made.
 */
export const replayMatch = (record: MatchRecord, rng: Rng): ReplayedMatch | null => {
  rng.restore(record.seed);
  let state = createMatch(
    record.seed,
    rng,
    record.mode,
    record.casters ?? null,
    record.opponentCasters ?? null,
  );
  const steps = [firstStep(state)];
  let liveIsAhead = false;
  for (const action of record.actions) {
    rng.restore(action.rngState);
    const after = applyAction(state, action, rng);
    if (after === state) return null;
    const step = stepBetween(state, after);
    if (step) steps.push(step);
    liveIsAhead = step === null;
    state = after;
  }
  return { state, steps, liveIsAhead };
};
